// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import zip from 'lodash/zip';

import {Timezone} from 'common/constants';
import {addToast} from 'common/components/toast-area/toast-manager';
import {getFormulaSection} from 'common/query-builder/insights-formula/utils';
import {getCountUsersOnceTypeFromEntrySelection} from 'common/query-builder/query-block/count-menu/util';
import {BEHAVIOR_MULTI_ATTRIBUTION_FILTER} from 'common/query-builder/query-block/properties-menu/constants';
import {QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {isSupportedMultiAttributionTypeForEntry} from 'common/query-builder/query-builder/util';
import {isMultiAttributionBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {ResourceType} from 'common/report/constants';
import {FUNNEL_MINUTE_CHART_MAX_HOUR_DURATION} from 'common/report/insights/models/constants';
import {getNumberOfHoursInTimeClause} from 'common/report/insights/models/time-clause';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {LiftType} from 'common/report/util/lift/types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {maybeModifySavedFormula} from 'common/util/formulas';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {getLocation} from 'common/util/url';
import {SCDOptionsType} from 'common/widgets/property-filter-menu/models/types';
import {
  ChartPlotStyle,
  ChartType,
  InsightsResourceType,
  MetricType,
  PRIMITIVE_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {MetricEntry} from 'common/query-builder/query-entry-section/types';

import {getFilterSection} from '../util/filters';
import {BehaviorShowClause as BehaviorShowClauseType} from '../util/types';
import {isBehaviorFilterClause, isTTCFilterClause} from '../models/filter-clause';
import {isBehaviorGroupClause, isTTCGroupClause} from '../models/group-clause';
import {Report} from '../models/report';
import {AllSectionTypes} from '../models/section';
import {BehaviorShowClause, ShowClauseClassType} from '../models/show-clause';
import {
  getMaxStepIndex,
  hasAnyOrderFunnel,
  hasFunnelConversionMetric,
  hasFunnelTimeToConvertMetric,
  hasOneFunnelMetric,
  hasRetentionRateMetric,
  isBehaviorShowClause,
  isFunnelShowClause,
  isFunnelTimeToConvertShowClause,
  isRetentionOnlyInsightsBookmark,
  isRetentionShowClause,
} from '../models/utils';
import {
  compareTimeUnits,
  getNumMultiAttributionProps,
  getNumShowClausesWithMultiAttribution,
  getSortConfig,
  XAU_TO_TIME_UNIT,
} from '../util';
import {hasValidMetricBookmarkShowClauses} from '../contexts/validation';
import {getShowSection} from '../util/events';
import {getSegmentSections} from '../util/segments';
import {hasSCDOptionsType} from '../util/scd-options';
import {getShowSectionFromMetricsEntries} from '../util/metrics';

/**
 * Removing an event from a funnel should update exclusion step ranges.
 */
export const updateReportFunnelExclusions = (report: Report) => {
  for (const showClause of report.sections.show.clauses) {
    if (isFunnelShowClause(showClause)) {
      const totalSteps = showClause.behavior.behaviors?.length || 0;
      (showClause.behavior.exclusions || []).forEach((x) => {
        if (x.steps.to > totalSteps) {
          x.steps.to = totalSteps;
        }
      });
    }
  }
};

/**
 * Makes sure bookmark sorting state matches the expected sorting state at all times.
 */
function updateReportSorting({
  report,
  headerCount,
  hasPivotTablesEnabled,
  canUseViewN,
}: {
  report: Report;
  headerCount: number;
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
}) {
  const updatedReport = report.clone();
  const {sections} = updatedReport;
  updatedReport.sorting = getSortConfig({
    headerCount,
    bookmarkSortConfig: updatedReport.sorting,
    hasPivotTablesEnabled,
    numMetrics: sections.show.clauses.length,
    canUseViewN,
    useDefaultViewNForNewSegments: true,
  });
  return updatedReport;
}

export function getHeaderCount({report}: {report: Report}): number {
  const {sections} = report;
  const hasDistributionAggregation = sections.show
    .getBehaviorClauses()
    .some((c) => c.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM);
  return 1 + sections.group.clauses.length + (hasDistributionAggregation ? 1 : 0);
}

export function updateReportFromQBSelection({
  selection,
  report,
  customEvents,
  hasPivotTablesEnabled = false,
  canUseViewN,
  metricsResource,
  behaviorsResource,
  projectTimezone,
  projectAsOfDate,
  metricLevelDataGroups = false,
  hasFirstClassMetrics = false,
}: {
  selection: QueryBuilderUpdateDetail;
  report: Report;
  customEvents: CustomEventDefinition[];
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  projectTimezone: Timezone;
  projectAsOfDate?: string;
  metricLevelDataGroups?: boolean;
  hasFirstClassMetrics?: boolean;
}): Report {
  const oldReport = report;

  report = report.clone();

  if (metricLevelDataGroups) {
    report.sections.metricLevelDataGroups = true;
  } else {
    report.sections.metricLevelDataGroups = false;
    // for default data group, qb returns an empty string
    if (selection.dataGroupId !== (report.sections.getDataGroupId() ?? ``)) {
      // TODO(j) Can this be removed? Validation done here may be overwritten by the section replacements below, real validation
      // appears to happen upstream in `emitUpdateEvent`
      report.sections.setDataGroupId({
        dataGroupId: selection.dataGroupId,
        customEvents,
        metricsResource,
        behaviorsResource,
      });
    }
  }

  /** while are are releasing a metrics-only QB, we will use one of these two functions to generate QB UI state */
  const getShowFn = hasFirstClassMetrics ? getShowSectionFromMetricsEntries : getShowSection;

  const showSection = getShowFn({
    entries: selection.entries as MetricEntry[],
    dataGroupId: selection.dataGroupId,
    attributionSelection: selection.attributionSelection,
    queryType: selection?.queryType,
  });
  const segmentSections = getSegmentSections(selection.groupBy);
  const updatedSections: AllSectionTypes[] = [
    showSection,
    getFilterSection(selection.filters, selection.filtersOperator),
    getFormulaSection(selection.formulas),
    ...segmentSections,
  ];

  updatedSections.forEach((updatedSection) => {
    report.sections = report.sections.replaceSection(updatedSection);
  });

  const maxStepIndex = getMaxStepIndex(report.sections.show.clauses);
  report.sections.filter.clauses.forEach((filter) => {
    if (isBehaviorFilterClause(filter) && filter.behavior.endStepNumber > maxStepIndex) {
      filter.behavior.startStepNumber = 0;
      filter.behavior.endStepNumber = 1;
    }
    if (isTTCFilterClause(filter) && filter.endStepNumber > maxStepIndex) {
      filter.startStepNumber = null;
      filter.endStepNumber = null;
    }
  });
  report.sections.group.clauses.forEach((group) => {
    if (isBehaviorGroupClause(group) && group.behavior.endStepNumber > maxStepIndex) {
      group.behavior.startStepNumber = 0;
      group.behavior.endStepNumber = 1;
    }
    if (isTTCGroupClause(group) && group.endStepNumber > maxStepIndex) {
      group.startStepNumber = null;
      group.endStepNumber = null;
    }
  });

  // If funnel metrics are removed, remove TTC filter and breakdown
  const removedFunnelMetric =
    oldReport.sections.show.clauses.some(isFunnelShowClause) && !showSection.clauses.some(isFunnelShowClause);
  if (removedFunnelMetric) {
    const hasTTCGroupClause = report.sections.group.clauses.some((group) => isTTCGroupClause(group));
    const hasTTCFilterClause = report.sections.filter.clauses.some((filter) => isTTCFilterClause(filter));
    if (hasTTCGroupClause || hasTTCFilterClause) {
      report.sections.group.clauses = report.sections.group.clauses.filter((group) => !isTTCGroupClause(group));
      report.sections.filter.clauses = report.sections.filter.clauses.filter((filter) => !isTTCFilterClause(filter));
      addToast({
        message: `Time to Convert is only available when there are funnel metrics.`,
        ctaHref: getLocation().href,
        cta: `Undo`,
      });
    }
  }

  updateReportFunnelExclusions(report);

  const cohortsChanged = !isEqual(report.sections.cohorts.clauses, oldReport.sections.cohorts.clauses);
  const groupsChanged = !isEqual(report.toBookmarkData().sections.group, oldReport.toBookmarkData().sections.group);
  const segmentsChanged = cohortsChanged || groupsChanged;

  const newNumMultiAttributionProps = getNumMultiAttributionProps(report.sections.group.clauses);
  const oldNumMultiAttributionProps = getNumMultiAttributionProps(oldReport.sections.group.clauses);
  const oldNumMultiAttributionModels = getNumShowClausesWithMultiAttribution(oldReport.sections.show.clauses);
  const newNumMultiAttributionModels = getNumShowClausesWithMultiAttribution(report.sections.show.clauses);
  const removedLastAttributionBreakdown = oldNumMultiAttributionProps > 0 && newNumMultiAttributionProps === 0;

  let attributionBreakdownRemoved = false;

  // Case when first attribution property is added
  if (oldNumMultiAttributionProps === 0 && newNumMultiAttributionProps > 0) {
    let incompatibleCountTypeFound = false;
    // Add attribution model to all metrics when first attribution property is added
    zip(report.sections.show.clauses, selection.entries).forEach(([clause, entry]) => {
      // check for placeholder clauses (e.g. placeholder event when loading report), since we don't want to add
      // attribution model to them
      if (hasValidMetricBookmarkShowClauses([clause.toBookmarkData()])) {
        if (isSupportedMultiAttributionTypeForEntry({entry, metricsResource, behaviorsResource})) {
          clause.measurement.multiAttribution = {...DefaultMultiAttribution};
        } else {
          incompatibleCountTypeFound = true;
        }
      }
    });
    if (incompatibleCountTypeFound) {
      addToast({
        message: `Multi attribution could not be added to some metrics due to incompatible count types.`,
        ctaHref: getLocation().href,
        cta: `Undo`,
      });
      if (getNumShowClausesWithMultiAttribution(report.sections.show.clauses) === 0) {
        report.sections.group.clauses.forEach((clause) => {
          if (isMultiAttributionBehavior(clause.behavior)) {
            delete clause.behavior;
          }
        });
      }
    }
  }

  // Case when the last attribution property is removed
  else if (removedLastAttributionBreakdown) {
    // Remove attribution models from metrics when all attribution property are removed
    report.sections.show.clauses.forEach((clause: ShowClauseClassType) => {
      delete clause.measurement.multiAttribution;
    });
    attributionBreakdownRemoved = true;
  }
  // Case when the last attribution model is removed with an attribution property
  else if (oldNumMultiAttributionModels > 0 && newNumMultiAttributionModels === 0 && newNumMultiAttributionProps > 0) {
    report.sections.group.clauses.forEach((clause) => {
      if (isMultiAttributionBehavior(clause.behavior)) {
        delete clause.behavior;
      }
    });
    attributionBreakdownRemoved = true;
  }

  // If the attribution breakdown removed we need to get rid of the attribution
  // filter.
  if (attributionBreakdownRemoved) {
    report.sections.filter.clauses = report.sections.filter.clauses.filter(
      (clause) => clause.value !== BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value,
    );
    // Remove any inline attribution filters
    report.sections.show.clauses
      .filter((clause) => isBehaviorShowClause(clause))
      .forEach((clause: BehaviorShowClause) => {
        clause.behavior.filters = (clause.behavior.filters || []).filter(
          (filter) => !isMultiAttributionBehavior(filter.behavior),
        );
      });
  }

  const addedNewMetric =
    // When loading Insights, it initially starts with 1 show clause that is incomplete, so when this incomplete
    // metric becomes valid, a metric has been added
    (!hasValidMetricBookmarkShowClauses(oldReport.toBookmarkData().sections.show) &&
      hasValidMetricBookmarkShowClauses(report.toBookmarkData().sections.show)) ||
    report.sections.show.clauses.length > oldReport.sections.show.clauses.length;
  // Check if a new metric was added, and add attribution model to it if the metric supports attribution
  if (
    newNumMultiAttributionProps > 0 &&
    addedNewMetric &&
    isSupportedMultiAttributionTypeForEntry({entry: last(selection.entries), metricsResource, behaviorsResource})
  ) {
    const newAttributionCapableMetric = last(report.sections.show.clauses);
    if (!newAttributionCapableMetric.measurement.multiAttribution) {
      newAttributionCapableMetric.measurement.multiAttribution = {...DefaultMultiAttribution};
    }
  }

  if (newNumMultiAttributionProps > 0) {
    report.sections.show.clauses.forEach((clause) => {
      if (!(clause instanceof BehaviorShowClause) || !clause.measurement.multiAttribution) {
        return;
      }
      if (clause.behavior.type === MetricType.Funnel) {
        // Multi-touch attribution in Funnels will always be against the Step 1 event
        clause.measurement.actionMode = `step`;
        clause.measurement.actionStep = 1;
      }
      if (clause.behavior.type === MetricType.Retention) {
        // Multi-touch attribution in Retention will always be against the first event
        clause.measurement.retentionSegmentationEvent = clause.behavior.behaviors[0].name;
      }
    });

    // If attribution breakdown is selected, ensure that attribution metrics with unique
    // users count has a "Count Users Once" segmentMethod type
    const updateSegmentMethod = (multiAttribution: MultiAttribution, clause: BehaviorShowClauseType): void => {
      if (clause.measurement.math === PRIMITIVE_MATH_TYPES.UNIQUE) {
        clause.measurement.segmentMethod = getCountUsersOnceTypeFromEntrySelection({
          selectedCountUsersOnceType: clause.measurement.segmentMethod,
          metricType: clause.behavior.type,
          multiAttribution,
        });
      }
    };

    report.sections.show.clauses.forEach((clause) => {
      if (clause instanceof BehaviorShowClause) {
        updateSegmentMethod(clause.measurement.multiAttribution, clause);
      } else {
        maybeModifySavedFormula({
          formulaClassObject: clause,
          metricsResource,
          behaviorsResource,
          updateReferencedMetricsFn: (clauses) =>
            clauses.forEach((referencedClause) => {
              updateSegmentMethod(clause.measurement.multiAttribution, referencedClause);
            }),
        });
      }
    });
  }

  if (
    report.sections.group.clauses.length === 0 ||
    // segmentMethod is a rarely needed property outside of attribution, so we should remove it along with the last
    // attribution breakdown
    removedLastAttributionBreakdown
  ) {
    const removeSegmentMethod = (clause: BehaviorShowClauseType) => {
      if (clause.measurement.segmentMethod) {
        delete clause.measurement.segmentMethod;
      }
    };

    report.sections.show.clauses.forEach((clause) => {
      if (clause instanceof BehaviorShowClause) {
        removeSegmentMethod(clause);
      } else {
        maybeModifySavedFormula({
          formulaClassObject: clause,
          metricsResource,
          behaviorsResource,
          updateReferencedMetricsFn: (clauses) => clauses.forEach(removeSegmentMethod),
        });
      }
    });
  }

  // Ensure that actionMode and actionStep are present for funnel metrics if there's a breakdown
  if (report.sections.group.clauses.length > 0 || report.sections.filter.clauses.length > 0) {
    const isFilterByUserOrCohortOnly = report.sections.filter.clauses.every((clause) => {
      return (
        clause.resourceType === InsightsResourceType.People ||
        clause.toPropertyFilter().resourceType === ResourceType.Cohort
      );
    });
    const isGroupByUserOrCohortOnly = report.sections.group.clauses.every(
      (clause) => clause.resourceType === InsightsResourceType.People || clause.isGroupByCohorts(),
    );
    if (isFilterByUserOrCohortOnly && isGroupByUserOrCohortOnly) {
      report.sections.show.clauses.forEach((clause) => {
        if (clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel) {
          delete clause.measurement.actionMode;
          delete clause.measurement.actionStep;
        }
      });
    } else {
      report.sections.show.clauses.forEach((clause) => {
        if (clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel) {
          const {actionMode, actionStep} = clause.measurement;
          const defaultActionMode = `step`;
          const defaultActionStep = 1;
          clause.measurement.actionMode = actionMode || defaultActionMode;
          clause.measurement.actionStep = actionStep || defaultActionStep;
        }
      });
    }
  } else {
    report.sections.show.clauses.forEach((clause) => {
      if (clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel) {
        clause.measurement.actionMode = null;
        clause.measurement.actionStep = null;
      }
    });
  }

  // disallowed chart types
  const {chartType, plotStyle} = report.displayOptions;
  switch (chartType) {
    case ChartType.Bar: {
      const isCohortClauseWithoutGroups =
        report.sections.group.hasGroupByCohortsClause() && !report.sections.group.hasGroupByPropertyClause();
      if (isCohortClauseWithoutGroups && plotStyle === ChartPlotStyle.Stacked) {
        // do not allow stacked bar for queries with cohorts and no groups
        report.displayOptions.plotStyle = ChartPlotStyle.Standard;
      }
      break;
    }
    case ChartType.FunnelTopPaths: {
      if (!hasAnyOrderFunnel(report.sections.show.getBehaviorClauses())) {
        report.displayOptions.chartType = ChartType.FunnelSteps;
      }
      break;
    }
    case ChartType.FunnelSteps: {
      if (!hasOneFunnelMetric(report.toBookmarkData())) {
        addToast({
          message: `Funnel steps chart is only available when there is one funnel metric only`,
          ctaHref: getLocation().href,
          cta: `Undo`,
        });
        report.displayOptions.chartType = ChartType.Line;
        report.displayOptions.plotStyle = ChartPlotStyle.Standard;
      }
      if (report.sections.show.clauses.some((show) => isFunnelTimeToConvertShowClause(show))) {
        report.displayOptions.chartType = ChartType.Bar;
      }
      break;
    }
    case ChartType.RetentionCurve: {
      if (!isRetentionOnlyInsightsBookmark(report.toBookmarkData())) {
        // href to current location will redirect to the current pre-change clean URL
        addToast({
          message: `Retention curve chart is only available when all metrics are retention metrics`,
          ctaHref: getLocation().href,
          cta: `Undo`,
        });
        report.displayOptions.chartType = ChartType.Line;
        report.displayOptions.plotStyle = ChartPlotStyle.Standard;
      }
    }
  }

  // correct time unit if clause type has changed
  const timeClause = report.sections.time.clauses[0];

  // TODO why dont we use the same helper that the chart header component uses?
  if (
    (report.isCohortsOverTime() || report.hasBehavioralFilterOrSegmentation()) &&
    [TimeUnit.Minute, TimeUnit.Hour].includes(timeClause.unit)
  ) {
    timeClause.unit = TimeUnit.Day;
  }

  // If retention metric is added and time unit is one of "minute", "hour", or "quarter",
  // change time unit to "day"
  const addedRetentionMetric =
    !oldReport.sections.show.clauses.some(isRetentionShowClause) && showSection.clauses.some(isRetentionShowClause);
  if (addedRetentionMetric && [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Quarter].includes(timeClause.unit)) {
    timeClause.unit = TimeUnit.Day;
  }

  // If a funnel metric is added and time unit is "minute" and duration is greater than 2 hours,
  // change time unit to "hour"
  const addedFunnelMetric =
    !oldReport.sections.show.clauses.some(isFunnelShowClause) && showSection.clauses.some(isFunnelShowClause);
  if (
    addedFunnelMetric &&
    (timeClause.unit as TimeUnit) === TimeUnit.Minute &&
    getNumberOfHoursInTimeClause(timeClause, projectTimezone, projectAsOfDate) > FUNNEL_MINUTE_CHART_MAX_HOUR_DURATION
  ) {
    timeClause.unit = TimeUnit.Hour;
  }

  if (report.sections.show.hasXauClauses()) {
    const maxTimeUnit = XAU_TO_TIME_UNIT[report.sections.show.lowestXauTimeUnit()];
    const minTimeUnit = TimeUnit.Day;
    const aboveMax = compareTimeUnits(maxTimeUnit, timeClause.unit) < 0;
    const belowMin = compareTimeUnits(minTimeUnit, timeClause.unit) > 0;
    if (aboveMax) {
      timeClause.unit = maxTimeUnit;
    } else if (belowMin) {
      timeClause.unit = minTimeUnit;
    }
  }

  const toastUndo = (message: string) =>
    addToast({
      message,
      // href to current location will redirect to the current pre-change clean URL
      ctaHref: getLocation().href,
      cta: `Undo`,
    });

  // If any of the show clause has rolling or cumulative calculation, change chart type to line chart
  if (
    (report.sections.show.hasRollingClauses() ||
      report.sections.show.hasCumulativeClauses() ||
      report.sections.show.hasCumulativeUniqueClauses()) &&
    ![ChartType.Line, ChartType.Column].includes(chartType)
  ) {
    toastUndo(`Rolling or cumulative calculations are only supported for line and column charts.`);
    report.displayOptions.chartType = ChartType.Line;
    report.displayOptions.plotStyle = ChartPlotStyle.Standard;
  }

  if (
    hasSCDOptionsType(SCDOptionsType.Any, report.toBookmarkData()) &&
    [ChartType.Line, ChartType.Column, ChartType.StackedLine, ChartType.StackedColumn].includes(chartType)
  ) {
    toastUndo(`The any value option for profile properties is not supported for line or column charts.`);
    report.displayOptions.chartType = ChartType.Bar;
    report.displayOptions.plotStyle = ChartPlotStyle.Standard;
  }

  if (plotStyle === ChartPlotStyle.Stacked || chartType === ChartType.Pie) {
    const chartName = plotStyle === ChartPlotStyle.Stacked ? `stacked ${chartType}` : chartType;
    if (hasFunnelConversionMetric(report.toBookmarkData())) {
      toastUndo(`Funnel conversion rate metric is not supported on ${chartName} chart.`);
      if (hasOneFunnelMetric(report.toBookmarkData())) {
        report.displayOptions.chartType = ChartType.FunnelSteps;
      } else if (chartType === ChartType.Pie) {
        report.displayOptions.chartType = ChartType.Line;
      }
      report.displayOptions.plotStyle = ChartPlotStyle.Standard;
    }
    if (hasFunnelTimeToConvertMetric(report.toBookmarkData())) {
      toastUndo(`Funnel time to convert metric is not supported on ${chartName} chart.`);
      if (chartType === ChartType.Pie) {
        report.displayOptions.chartType = ChartType.Line;
      }
      report.displayOptions.plotStyle = ChartPlotStyle.Standard;
    }
    if (hasRetentionRateMetric(report.toBookmarkData())) {
      toastUndo(`Retention rate metric is not supported on ${chartName} chart.`);
      if (isRetentionOnlyInsightsBookmark(report.toBookmarkData())) {
        report.displayOptions.chartType = ChartType.RetentionCurve;
      } else if (chartType === ChartType.Pie) {
        report.displayOptions.chartType = ChartType.Line;
      }
      report.displayOptions.plotStyle = ChartPlotStyle.Standard;
    }
  }

  const liftType = report.liftComparison?.type;
  // If we change breakdowns, we don't want to compare against a baseline value that might not exist, so clear lift
  if (liftType === LiftType.Segment && segmentsChanged) {
    report.liftComparison = null;
  }

  if (liftType === LiftType.Overall && segmentsChanged && report.sections.group.clauses.length === 0) {
    if ([MetricType.Funnel, MetricType.Retention].includes(selection.queryType)) {
      report.liftComparison = null;
    } else {
      if (report.sections.show.clauses.length === 1) {
        report.liftComparison = null;
      }
    }
  }

  // clean up previous statSigControl so funnels breakdown change won't break
  if (segmentsChanged) {
    report.displayOptions.statSigControl = [];
    report.legend.overrides = {};
    delete report.legend.filteredBreakdownSegments;
  }

  const headerCount = getHeaderCount({report});
  const oldHeaderCount = getHeaderCount({report: oldReport});
  // update sort config if the length of group section or show section changes
  const shouldUpdateSorting = headerCount !== oldHeaderCount;
  if (shouldUpdateSorting) {
    report = updateReportSorting({report, hasPivotTablesEnabled, canUseViewN, headerCount});
  }

  return report;
}
